<script setup lang="ts">
import { RouterLink } from 'vue-router'
import Icon from '@/components/icons/Icon.vue'
import HeroDemo from '@/components/home/HeroDemo.vue'
</script>

<template>
  <section class="hero">
    <!-- Subtle dot-grid texture behind the hero content -->
    <div class="hero__grid" aria-hidden="true"></div>
    <div class="container hero__inner">
      <div class="hero__content">
        <div class="hero__badge">
          <Icon name="sparkles" :size="14" />
          <span>Free, independent, South African</span>
        </div>
        <h1 class="hero__title">Smart South African calculators, made simple</h1>
        <p class="hero__lead">
          Free, fast tools for salary, tax, fuel, VAT, bonds, solar, cars and more. No sign-up, no
          tracking of your inputs — just clear answers you can understand and share.
        </p>
        <div class="hero__actions">
          <RouterLink to="/calculators" class="btn btn--accent">
            Browse calculators
            <Icon name="arrow-right" :size="18" />
          </RouterLink>
          <RouterLink to="/about" class="btn btn--secondary">How it works</RouterLink>
        </div>
      </div>
      <div class="hero__demo" aria-label="Live salary estimate demo">
        <div class="hero__demo-glow" aria-hidden="true"></div>
        <HeroDemo />
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  padding: var(--space-12) 0;
  background:
    radial-gradient(circle at 90% 20%, rgb(217 119 6 / 0.08), transparent 35%),
    radial-gradient(circle at 10% 80%, rgb(10 92 86 / 0.08), transparent 35%), var(--color-bg);
  border-bottom: 1px solid var(--color-border);
  overflow: hidden;
}

.hero__grid {
  position: absolute;
  inset: 0;
  opacity: 0.4;
  background-image: radial-gradient(var(--color-border-strong) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, black 30%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 30%, transparent 100%);
}

.hero__inner {
  position: relative;
  display: grid;
  gap: var(--space-10);
  align-items: center;
}

@media (min-width: 900px) {
  .hero__inner {
    grid-template-columns: 1fr minmax(340px, 420px);
    min-height: 460px;
  }
}

.hero__badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  background-color: rgb(10 92 86 / 0.1);
  color: var(--color-primary-dark);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  margin-bottom: var(--space-4);
}

.hero__title {
  font-size: var(--text-3xl);
  font-weight: var(--font-extrabold);
  line-height: var(--leading-tight);
  letter-spacing: -0.03em;
  max-width: 18ch;
}

.hero__lead {
  margin-top: var(--space-4);
  font-size: var(--text-lg);
  color: var(--color-text-muted);
  max-width: 52ch;
}

.hero__actions {
  margin-top: var(--space-6);
  display: flex;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.hero__demo {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 899px) {
  .hero__demo {
    order: -1;
  }
}

.hero__demo-glow {
  position: absolute;
  inset: -30px;
  background: radial-gradient(circle, rgb(13 148 136 / 0.18), transparent 60%);
  pointer-events: none;
  animation: pulse-glow 6s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-md);
  font-weight: var(--font-semibold);
  text-decoration: none;
  border: 1px solid transparent;
  transition:
    background-color 0.15s ease,
    transform 0.05s ease,
    box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn--accent {
  background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
  color: var(--color-text);
  box-shadow: 0 4px 14px rgb(217 119 6 / 0.35);
}

.btn--accent:hover {
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-dark));
  color: white;
  box-shadow: 0 6px 18px rgb(217 119 6 / 0.45);
}

.btn--secondary {
  background-color: var(--color-surface);
  color: var(--color-primary);
  border-color: var(--color-border-strong);
}

.btn--secondary:hover {
  background-color: var(--color-surface-raised);
}

@media (min-width: 768px) {
  .hero__title {
    font-size: var(--text-4xl);
  }
}

@media (min-width: 1100px) {
  .hero__title {
    font-size: var(--text-5xl);
  }
}
</style>
